import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Drawer, Icon, Table, Menu, Dropdown, Tooltip } from 'antd';
import { compare } from 'natural-orderby';
import get from 'lodash/get';

import { loadTaskGrid, changeStatus, createTaskRecordAndUpdateStatus, addComment, createTaskRecordAndComment, changeStatusAll, removeComment } from './actions';
import TableSkeleton from './TableSkeleton';
import { getBackgroundColorCell, handleDrawerClose } from './helpers';
import CommentsModal from './CommentsModal';

import './styles.css';

const { Column } = Table;
const months = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

const DrawerForm = ({ data, date, visible, onClose }) => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [currentRecordTaskId, setCurrentRecordTaskId] = useState(null);
  const [isHistoryModalVisible, setIsHistoryModalVisible] = useState(false);
  const [currentRecordTaskComments, setCurrentRecordTaskComments] = useState([]);
  const dispatch = useDispatch();
  const customersById = useSelector((state) => state.customersById);

  const updateTasksCardsFromGrid = useCallback((customers, currentMonth, taskId) => {
    dispatch({
      type: 'CALENDAR_CARDS_TASKS_UPDATE_FROM_GRID',
      payload: { customers, currentMonth, taskId },
    });
  }, [dispatch]);

  const updateContextTasksCardsFromGrid = useCallback((customers, currentMonth, taskId) => {
    dispatch({
      type: 'CONTEXT_CALENDAR_CARDS_TASKS_UPDATE_FROM_GRID',
      payload: { customers, currentMonth, taskId },
    });
  }, [dispatch]);

  const handleLoadTaskGrid = useCallback(() => {
    if (data) {
      loadTaskGrid(data.id, date, customersById, setLoading, setCustomers);
    }
  }, [data, date, customersById]);

  useEffect(() => {
    handleLoadTaskGrid();
  }, [handleLoadTaskGrid]);

  const handleDrawerCloseCallback = useCallback(() => {
    handleDrawerClose(data, date, customers, updateContextTasksCardsFromGrid, updateTasksCardsFromGrid, onClose, setLoading, setCustomers);
  }, [data, date, customers, updateContextTasksCardsFromGrid, updateTasksCardsFromGrid, onClose, setLoading, setCustomers]);

  const showCommentModal = useCallback((record_task_id, record, monthIndex) => {
    setCurrentRecordTaskId({ record_task_id, record, monthIndex });
    setIsModalVisible(true);
  }, []);

  const handleModalCancel = useCallback(() => {
    setIsModalVisible(false);
    setCurrentRecordTaskId(null);
  }, []);

  const showCommentHistoryModal = useCallback((record_task) => {
    setCurrentRecordTaskComments(record_task.comments);
    setCurrentRecordTaskId(record_task.id);
    setIsHistoryModalVisible(true);
  }, []);

  const memoizedColumns = useMemo(() => months.map((month, monthIndex) => (
    <Column
      key={monthIndex + 1}
      title={
        <Dropdown
          overlay={
            <Menu onClick={(e) => changeStatusAll(e.key, monthIndex + 1, data, date, setLoading, loadTaskGrid)}>
              <Menu.Item key="priority">
                <Icon type="star" theme="twoTone" twoToneColor="#fadb14" /> Marcar todos como prioridade
              </Menu.Item>
              <Menu.Item key="uncheck_priority">
                <Icon type="star" /> Remover prioridade de todos
              </Menu.Item>
            </Menu>
          }
          trigger={['click']}
        >
          <span className="ant-dropdown-link">
            {month} <Icon type="down" />
          </span>
        </Dropdown>
      }
      dataIndex={monthIndex + 1}
      width={80}
      className="grid-month"
      render={(text, record) => {
        const record_task = record.months[monthIndex + 1];
        const customer = customersById[record.customer_id];
        if (!record_task) return false;
        const backgroundColor = getBackgroundColorCell(record, monthIndex + 1, customer);
        const isDisabled = backgroundColor === '#d9d9d9';

        if (isDisabled) {
          return (
            <Tooltip title="Periodo em que o escritório não é responsável">
              <div
                style={{
                  backgroundColor,
                  height: '100%',
                  width: '100%',
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  left: 0,
                  right: 0,
                }}
              />
            </Tooltip>
          );
        }

        return (
          <Dropdown
            overlay={
              !isDisabled && (
                <Menu
                  onClick={(e) => {
                    const recordDueDate = record_task.due_date;
                    const taskDueDay = data.due_date.split('-')[2];
                    if (e.key === 'comments') {
                      setCurrentRecordTaskComments(record_task.comments || []);
                      setCurrentRecordTaskId({ 
                        record_task_id: record_task.id, 
                        record: { ...record_task, customer_id: record.customer_id }, 
                        monthIndex: monthIndex + 1 
                      });
                      setIsModalVisible(true);
                    } else if (!record_task.id) {
                      createTaskRecordAndUpdateStatus({
                        task_id: data.id,
                        customer_id: record.customer_id,
                        due_date: `${recordDueDate}-${taskDueDay}`,
                        status: e.key,
                      }, setCustomers);
                    } else {
                      changeStatus(record_task.id, e.key, setCustomers);
                    }
                  }}
                >
                  {get(record_task, 'executed_at', null) === null && (
                    <Menu.Item key="executed">
                      <Icon type="check" /> Marcar como executado
                    </Menu.Item>
                  )}
                  {record_task.executed_at !== null && !record_task.revised_at && (
                    <Menu.Item key="uncheck_executed">
                      <Icon type="close" /> Desmarcar como executado
                    </Menu.Item>
                  )}
                  {!record_task.revised_at && (
                    <Menu.Item key="revised">
                      <Icon type="check" /> Marcar como revisado
                    </Menu.Item>
                  )}
                  {record_task.revised_at !== null && (
                    <Menu.Item key="uncheck_revised">
                      <Icon type="close" /> Desmarcar como revisado
                    </Menu.Item>
                  )}
                  <Menu.Divider />
                  {record_task.priority === 0 && (
                    <Menu.Item key="priority">
                      <Icon type="star" theme="twoTone" twoToneColor="#fadb14" /> Marcar como prioridade
                    </Menu.Item>
                  )}
                  {record_task.priority === 1 && (
                    <Menu.Item key="uncheck_priority">
                      <Icon type="star" /> Desmarcar como prioridade
                    </Menu.Item>
                  )}
                  <Menu.Item key="comments">
                    <Icon type="message" /> Comentários
                  </Menu.Item>
                </Menu>
              )
            }
            trigger={['contextMenu', 'click']}
          >
            <div
              style={{
                userSelect: 'none',
                backgroundColor,
                height: '100%',
                width: '100%',
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: 0,
                right: 0,
              }}
            >
              {record_task.priority === 1 && <Icon type="star" theme="twoTone" twoToneColor="#fadb14" />}
              {record_task.comments && Array.isArray(record_task.comments) && record_task.comments.length > 0 && (
                <Tooltip title={`${record_task.comments.length} comentário(s)`}>
                  <Icon type="message" theme="twoTone" twoToneColor="#1890ff" />
                </Tooltip>
              )}
            </div>
          </Dropdown>
        );
      }}
    />
  )), [changeStatusAll, customersById, data ? data.due_date : null, createTaskRecordAndUpdateStatus, changeStatus, showCommentModal, showCommentHistoryModal]);

  return (
    <div>
      {data && (
        <Drawer
          title={data.name}
          width="calc(100% - 100px)"
          placement="right"
          onClose={handleDrawerCloseCallback}
          maskClosable={false}
          visible={visible}
          style={{ height: '100%', overflow: 'auto', paddingBottom: 53 }}
        >
          <div className="table-clabs">
            {loading ? (
              <TableSkeleton />
            ) : (
              <Table
                rowKey="customer_id"
                dataSource={customers}
                pagination={false}
                bordered
                rowClassName="row-clabs-grid"
                size="small"
                scroll={{ x: '1350px', y: `${window.innerHeight - 175}px` }}
              >
                <Column
                  title="Cod"
                  dataIndex="customer.cod"
                  key="cod"
                  width={90}
                  sorter={(a, b) => compare()(a.customer.cod, b.customer.cod)}
                  sortDirections={['descend', 'ascend']}
                  defaultSortOrder="ascend"
                />
                <Column title="Cliente" dataIndex="customer.name" key="customer" width={300} ellipsis />
                {memoizedColumns}
              </Table>
            )}
          </div>
        </Drawer>
      )}
      <CommentsModal
        visible={isModalVisible}
        onCancel={handleModalCancel}
        recordTaskId={currentRecordTaskId ? currentRecordTaskId.record_task_id : null}
        record={currentRecordTaskId ? currentRecordTaskId.record : null}
        monthIndex={currentRecordTaskId ? currentRecordTaskId.monthIndex : null}
        addComment={addComment}
        createTaskRecordAndComment={createTaskRecordAndComment}
        removeComment={removeComment}
        data={data}
        setCustomers={setCustomers}
        customers={customers}
        comments={currentRecordTaskComments}
      />
    </div>
  );
};

export default DrawerForm;
